import { Button } from '@tvg/fuse-design/buttons';
import { Divider } from '@tvg/fuse-design/divider';
import { GridItem, GridRow, gridConfig } from '@tvg/fuse-design/responsive-grid';
import IconFilterList from '@tvg/fuse-icons/lib/FilterList';
import { useDrawerState } from 'client/state/Drawer';
import { useFacetListState } from 'client/state/FacetList';
import { FILTER_DRAWER_BUTTON_ID } from 'client/utils/consts';
import React, { memo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import ProductsPerPage from './ProductsPerPage/ProductsPerPage';
import ResultCount from './ResultCount';
import SortOrdersDropdown from './SortOrdersDropdown';

const TopRowStyled = styled(GridRow)`
  .filter-button-row {
    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      display: none;
    }
  }
`;

const StyledGridItem = styled(GridItem)`
  display: grid;
  &.results-grid-item-mobile {
    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      display: none;
    }
  }

  &.results-grid-item-desktop {
    @media screen and (max-width: ${gridConfig.m.maxWidth}) {
      display: none;
    }
  }
`;

const DividerStyled = styled(Divider)`
  &.results-divider-mobile {
    @media screen and (max-width: ${gridConfig.m.maxWidth}) {
      padding-top: var(--spacing-space8);
    }
  }

  &.results-divider-desktop {
    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      padding-top: var(--spacing-space16);
      padding-bottom: 0;
      margin-bottom: var(--spacing-space4);
    }
  }
`;

const ResultWrapper = styled.div`
  & {
    display: inline-flex;
    align-self: center;
  }
`;

const ButtonStyled = styled(Button)`
  && {
    display: flex;
    padding: var(--spacing-space8) var(--spacing-space8) var(--spacing-space8) var(--spacing-space12);
    align-self: stretch;
    justify-content: flex-start;
    color: var(--grey-grey-900, #545454);
    font-weight: 500;
    border-radius: var(--spacing-space4);
    border: var(--border-width-xs) solid var(--grey-grey-700, #878787);
    background: var(--base-white, #fff);
    margin: 0;

    &.fuse-styled-button:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible,
    &:active {
      outline: var(--spacing-global-space1) solid var(--color-functional-focus);
      outline-offset: -1px;
    }
  }
`;

function TopRowComponent() {
  const toggleDrawerOpen = useDrawerState((s) => s.toggleDrawerOpen);
  const toggleAriaHidden = useDrawerState((s) => s.toggleAriaHidden);
  const toggleShowViewProducts = useDrawerState((s) => s.toggleShowViewProducts);
  const setActiveFacet = useFacetListState((s) => s.setActiveFacet);
  const filterRef = useRef<HTMLElement>(null);
  // useEffect(() => {
  //   if (isFocusFilterButton) {
  //     setTimeout(() => {
  //       filterRef.current?.focus();
  //       setIsFocusFilterButton(false);
  //     }, 100);
  //   }
  // }, [isFocusFilterButton, setIsFocusFilterButton]);

  const handleFilterClick = useCallback(() => {
    toggleAriaHidden.off();
    toggleShowViewProducts.off();
    toggleDrawerOpen.on();
    setActiveFacet();
  }, [setActiveFacet, toggleAriaHidden, toggleDrawerOpen, toggleShowViewProducts]);

  return (
    <TopRowStyled className="row">
      <GridItem xxs="6" className="filter-button-row">
        <ButtonStyled
          id={FILTER_DRAWER_BUTTON_ID}
          icon={<IconFilterList />}
          variant="tertiary"
          size="large"
          fullWidth
          onClick={handleFilterClick} // TODO: Need to set focus into the drawer to hear "Enter the dialog" from thes screen reader
          data-testid="toggle-facet__button"
          ref={filterRef}
        >
          Filter
        </ButtonStyled>
      </GridItem>

      <GridItem xxs="6" className="filter-button-row">
        <SortOrdersDropdown showInMobile />
      </GridItem>

      <StyledGridItem xxs="12" className="filter-button-row results-grid-item-mobile">
        <DividerStyled className="results-divider-mobile" />
      </StyledGridItem>

      <GridItem xxs="12" l="12">
        <GridRow>
          <GridItem xxs="12" l="8">
            <ResultWrapper>
              <ResultCount position="top" />
              <ProductsPerPage position="top" />
            </ResultWrapper>
          </GridItem>
          <GridItem l="4">
            <SortOrdersDropdown />
          </GridItem>
        </GridRow>
      </GridItem>

      <StyledGridItem xxs="12" className="results-grid-item-desktop">
        <DividerStyled className="results-divider-desktop" />
      </StyledGridItem>
    </TopRowStyled>
  );
}

export const TopRow = memo(TopRowComponent);
